import type { FC } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Form, Modal } from 'react-bootstrap';
import { useAuth } from '@hooks/useAuth';
import Layout from '@components/Layout';

// Validation schemas
const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

const signupSchema = z
  .object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    password_confirm: z.string(),
    first_name: z.string().min(1, 'First name is required'),
    last_name: z.string().min(1, 'Last name is required'),
  })
  .refine((data) => data.password === data.password_confirm, {
    message: "Passwords don't match",
    path: ['password_confirm'],
  });

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});

const resetPasswordSchema = z
  .object({
    code: z.string().min(1, 'Verification code is required'),
    new_password: z.string().min(8, 'Password must be at least 8 characters'),
    confirm_password: z.string(),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords don't match",
    path: ['confirm_password'],
  });

type LoginFormData = z.infer<typeof loginSchema>;
type SignupFormData = z.infer<typeof signupSchema>;
type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

const LoginPage: FC = () => {
  const navigate = useNavigate();
  const { login, signup, forgotPassword, confirmPasswordReset } = useAuth();
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  const {
    register: registerLogin,
    handleSubmit: handleLoginSubmit,
    formState: { errors: loginErrors, isSubmitting: isLoggingIn },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const {
    register: registerSignup,
    handleSubmit: handleSignupSubmit,
    formState: { errors: signupErrors, isSubmitting: isSigningUp },
    reset: resetSignup,
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
  });

  const {
    register: registerForgotPassword,
    handleSubmit: handleForgotPasswordSubmit,
    formState: { errors: forgotPasswordErrors, isSubmitting: isSendingCode },
    reset: resetForgotPassword,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const {
    register: registerResetPassword,
    handleSubmit: handleResetPasswordSubmit,
    formState: { errors: resetPasswordErrors, isSubmitting: isResettingPassword },
    reset: resetResetPassword,
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onLogin = async (data: LoginFormData) => {
    try {
      await login(data.email, data.password);
      navigate('/portal');
    } catch (error) {
      // Error is handled in AuthContext with toast
      console.error('Login failed:', error);
    }
  };

  const onSignup = async (data: SignupFormData) => {
    try {
      await signup(data.email, data.password, data.first_name, data.last_name);
      setShowSignupModal(false);
      setShowVerificationModal(true);
      resetSignup();
    } catch (error) {
      // Error is handled in AuthContext with toast
      console.error('Signup failed:', error);
    }
  };

  const onForgotPassword = async (data: ForgotPasswordFormData) => {
    try {
      await forgotPassword(data.email);
      setResetEmail(data.email);
      setShowForgotPasswordModal(false);
      setShowResetPasswordModal(true);
      resetForgotPassword();
    } catch (error) {
      // Error is handled in AuthContext with toast
      console.error('Forgot password failed:', error);
    }
  };

  const onResetPassword = async (data: ResetPasswordFormData) => {
    try {
      await confirmPasswordReset(resetEmail, data.code, data.new_password);
      setShowResetPasswordModal(false);
      setResetEmail('');
      resetResetPassword();
    } catch (error) {
      // Error is handled in AuthContext with toast
      console.error('Password reset failed:', error);
    }
  };

  // Handle Enter key in login form
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLoginSubmit(onLogin)();
    }
  };

  return (
    <Layout>
      <div className="container" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
        <div className="row top-row">
          <div className="col-12 bg-odd-header text-center">
            <h2 className="text-white">Login</h2>
          </div>
        </div>

        <Form onSubmit={handleLoginSubmit(onLogin)}>
          <div className="row">
            <div className="col-12 col-md-2 bg-odd-content d-flex align-items-center justify-content-md-end justify-content-start">
              <label className="h5 h2-md mb-0">Email:</label>
            </div>
            <div className="col-12 col-md-8 bg-light p-3">
              <Form.Control
                type="text"
                {...registerLogin('email')}
                isInvalid={!!loginErrors.email}
                onKeyDown={handleKeyDown}
              />
              <Form.Control.Feedback type="invalid">{loginErrors.email?.message}</Form.Control.Feedback>
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-md-2 bg-odd-content d-flex align-items-center justify-content-md-end justify-content-start">
              <label className="h5 h2-md mb-0">Password:</label>
            </div>
            <div className="col-12 col-md-8 bg-light p-3">
              <Form.Control
                type="password"
                {...registerLogin('password')}
                isInvalid={!!loginErrors.password}
                onKeyDown={handleKeyDown}
              />
              <Form.Control.Feedback type="invalid">{loginErrors.password?.message}</Form.Control.Feedback>
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-md-2 bg-odd-content"></div>
            <div className="col-12 col-md-8 bg-light p-3">
              <div className="company-text d-flex flex-column flex-md-row gap-2">
                <Button type="submit" variant="primary" size="lg" disabled={isLoggingIn}>
                  {isLoggingIn ? 'Logging in...' : 'Login'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  onClick={() => setShowSignupModal(true)}
                >
                  Sign up
                </Button>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-md-2 bg-odd-content"></div>
            <div className="col-12 col-md-8 bg-light p-3">
              <a href="#" onClick={(e) => { e.preventDefault(); setShowForgotPasswordModal(true); }}>
                Forgot Password?
              </a>
            </div>
          </div>
        </Form>
      </div>

      {/* Signup Modal */}
      <Modal show={showSignupModal} onHide={() => setShowSignupModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Extra signup info</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSignupSubmit(onSignup)}>
            <Form.Group className="mb-3">
              <Form.Label>Email:</Form.Label>
              <Form.Control type="email" {...registerSignup('email')} isInvalid={!!signupErrors.email} />
              <Form.Control.Feedback type="invalid">{signupErrors.email?.message}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>First Name:</Form.Label>
              <Form.Control
                type="text"
                {...registerSignup('first_name')}
                isInvalid={!!signupErrors.first_name}
              />
              <Form.Control.Feedback type="invalid">{signupErrors.first_name?.message}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Last Name:</Form.Label>
              <Form.Control
                type="text"
                {...registerSignup('last_name')}
                isInvalid={!!signupErrors.last_name}
              />
              <Form.Control.Feedback type="invalid">{signupErrors.last_name?.message}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password:</Form.Label>
              <Form.Control
                type="password"
                {...registerSignup('password')}
                isInvalid={!!signupErrors.password}
              />
              <Form.Control.Feedback type="invalid">{signupErrors.password?.message}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm Password:</Form.Label>
              <Form.Control
                type="password"
                {...registerSignup('password_confirm')}
                isInvalid={!!signupErrors.password_confirm}
              />
              <Form.Control.Feedback type="invalid">
                {signupErrors.password_confirm?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowSignupModal(false)}>
                Close
              </Button>
              <Button type="submit" variant="primary" disabled={isSigningUp}>
                {isSigningUp ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Verification Modal */}
      <Modal show={showVerificationModal} onHide={() => setShowVerificationModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Please visit the verification link sent to your email.</Modal.Title>
        </Modal.Header>
        <Modal.Body></Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowVerificationModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Forgot Password Modal */}
      <Modal show={showForgotPasswordModal} onHide={() => setShowForgotPasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Forgot Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Enter your email address and we'll send you a verification code to reset your password.</p>
          <Form onSubmit={handleForgotPasswordSubmit(onForgotPassword)}>
            <Form.Group className="mb-3">
              <Form.Label>Email:</Form.Label>
              <Form.Control
                type="email"
                {...registerForgotPassword('email')}
                isInvalid={!!forgotPasswordErrors.email}
              />
              <Form.Control.Feedback type="invalid">
                {forgotPasswordErrors.email?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowForgotPasswordModal(false)}>
                Close
              </Button>
              <Button type="submit" variant="primary" disabled={isSendingCode}>
                {isSendingCode ? 'Sending...' : 'Send Code'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Reset Password Modal */}
      <Modal show={showResetPasswordModal} onHide={() => setShowResetPasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Enter the verification code sent to your email and your new password.</p>
          <Form onSubmit={handleResetPasswordSubmit(onResetPassword)}>
            <Form.Group className="mb-3">
              <Form.Label>Verification Code:</Form.Label>
              <Form.Control
                type="text"
                {...registerResetPassword('code')}
                isInvalid={!!resetPasswordErrors.code}
              />
              <Form.Control.Feedback type="invalid">{resetPasswordErrors.code?.message}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>New Password:</Form.Label>
              <Form.Control
                type="password"
                {...registerResetPassword('new_password')}
                isInvalid={!!resetPasswordErrors.new_password}
              />
              <Form.Control.Feedback type="invalid">
                {resetPasswordErrors.new_password?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm Password:</Form.Label>
              <Form.Control
                type="password"
                {...registerResetPassword('confirm_password')}
                isInvalid={!!resetPasswordErrors.confirm_password}
              />
              <Form.Control.Feedback type="invalid">
                {resetPasswordErrors.confirm_password?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowResetPasswordModal(false)}>
                Close
              </Button>
              <Button type="submit" variant="primary" disabled={isResettingPassword}>
                {isResettingPassword ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Layout>
  );
};

export default LoginPage;
